import React, { useEffect, useRef, useState } from "react";
import { StyledCertifications } from "./Certifications.styled";
import {
  CATEGORY_LABEL,
  CERTIFICATIONS_LABEL,
  CERTIFICATION_LABEL,
  DELETE_CERTIFICATION_CONFIRM_MESSAGE,
  DELETE_CERTIFICATION_LABEL,
  DESCRIPTION_LABEL,
  EMPTY_CERTIFICATIONS_MESSAGE,
  EMPTY_STATE_CERTIFICATIONS_MESSAGE,
  EMPTY_STRING,
  EXECUTION_MODE_LABEL,
  GROUP_TARGETS_LABEL,
  INTERNAL_GRADE_IT_LABEL,
  NAME_LABEL,
  RECIPIENT_LABEL,
  URL_LABEL,
  VALIDITY_LABEL,
} from "../../../constants/Dictionary";
import certificatesImage from "../../../images/certificates.png";
import { ReactComponent as AddIcon } from "../../../icons/add.svg";
import { EmptyState } from "../../../components/EmptyState/EmptyState";
import {
  Button,
  ButtonSizes,
  ButtonVariants,
} from "../../../components/core/Button/Button";
import { CertificationModal } from "./CertificationModal/CertificationModal";
import { useDebounce } from "use-debounce";
import {
  AlignmentEnum,
  FilterProps,
  FontWeightEnum,
} from "../../../types/AppTypes";
import {
  CERTIFICATIONS_PAGE_SIZE,
  PermissionsEnum,
} from "../../../constants/AppConstants";
import { Certification } from "../../../types/api/certifications.type";
import { Link, LinkSizeEnum } from "../../../components/core/Link/Link";
import { DoubleStructure } from "../../../components/core/DoubleStructure/DoubleStructure";
import { BodyTyp } from "../../../components/Typography/Typography";
import SearchInput from "../../../components/core/SearchInput/SearchInput";
import { Loader } from "../../../components/core/Loader/Loader";
import { getCertificationsTableColumns } from "../../../constants/TableColumns";
import { PopupConfirm } from "../../../components/PopupConfirm/PopupConfirm";
import { FiltersPanel } from "../../../components/FiltersPanel/FiltersPanel";
import { getCertificationsFiltersTitles } from "../../../constants/FilterTitles";
import { roleHasPermission } from "../../../utils/roleAuth.utils";
import { useAppSelector } from "../../../hooks/store.hook";
import { FiltersSelected } from "../../../components/FiltersSelected/FiltersSelected";
import { findLabelFromValue } from "../../../utils/orgFunctions.utils";
import {
  TextBoxGrid,
  TextBox,
} from "../../../components/TextBoxGrid/TextBoxGrid.component";
import TargetGroups from "../../../components/TargetGroups/TargetGroups";
import { Table } from "../../../components/core/Table/Table";
import LoadingThumbnail from "../../../components/LoadingThumbnail/LoadingThumbnail";
import { useDispatch } from "react-redux";
import {
  setFiltersCertification,
  setPaginationCertification,
  setSearchCertification,
} from "../../../stores/actions/formation.actions";
import { useDimensions } from "../../../hooks/useDimensions.hook";
import {
  useDeleteFormationCertification,
  useFormationCertification,
  useFormationCertificationFilters,
} from "../../../hooks/formationCertification.hook";

type CertificationsProps = {
  addLink: (component: React.ReactNode) => void;
};

export const Certifications = ({ addLink }: CertificationsProps) => {
  const dispatch = useDispatch();
  const { profile } = useAppSelector((state) => state.user);
  const { orgSettings } = useAppSelector((state) => state.config);
  const {
    filtersCertifications,
    searchCertifications,
    paginationCertifications,
  } = useAppSelector((state) => state.formation.certifications);
  const [openCertificationModal, setOpenCertificationModal] = useState(false);
  const [searchDebouncedCertification] = useDebounce(searchCertifications, 500);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState<Certification | null>(
    null,
  );
  const [hasData, setHasData] = useState<boolean | null>(null);

  const { isMobile } = useDimensions();

  const { data: filtersData, isLoading: filterLoading } =
    useFormationCertificationFilters();

  const joinFilters = Object.values(filtersData ?? []).flat();

  const { data, isLoading, isFetching } = useFormationCertification({
    filtersCertifications,
    paginationCertifications,
    searchDebouncedCertification,
  });
  const { mutateAsync: deleteCertificationApi, isPending: isDeleting } =
    useDeleteFormationCertification();

  const handleFilters = (filters: FilterProps[]) => {
    dispatch(setPaginationCertification(0));
    dispatch(setFiltersCertification(filters));
  };

  const onEdit = (record: Certification) => {
    setSelectedRecord(record);
    setOpenCertificationModal(true);
  };

  const onDelete = (record: Certification) => {
    setSelectedRecord(record);
    setOpenDeleteModal(true);
  };

  const deleteCertification = async (id: number) => {
    await deleteCertificationApi(id);

    setOpenDeleteModal(false);
    setSelectedRecord(null);
  };

  const filtersAreActive = filtersCertifications.length > 0;

  useEffect(() => {
    if (!isLoading && data && hasData === null) {
      if (filtersAreActive || searchCertifications.length > 0) {
        setHasData(true);
        return;
      }
      const newHasData = data.pagination.total > 0;
      setHasData(newHasData);

      if (newHasData) {
        addLink(
          <Link
            onClick={() => setOpenCertificationModal(true)}
            icon={<AddIcon />}
            size={LinkSizeEnum.SMALL}
            disabled={isLoading}
          >
            {CERTIFICATION_LABEL}
          </Link>,
        );
      }
    }
  }, [data, hasData, isLoading]);

  useEffect(() => {
    return () => {
      addLink(null);
    };
  }, []);

  const isFirstRender = useRef(true);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    dispatch(setPaginationCertification(0));
  }, [searchDebouncedCertification]);

  const calcTableHeight = (): string => {
    let defaultHeight = 360;

    if (filtersAreActive) {
      defaultHeight += 52;
    }

    return `calc(100dvh - ${defaultHeight}px)`;
  };

  const expandedRow = (record: Certification): React.ReactElement | null => {
    return (
      <>
        {isMobile && (
          <TextBoxGrid
            numCol={2}
            items={[
              { label: VALIDITY_LABEL, text: record.validityPeriod },
              {
                label: CATEGORY_LABEL,
                text: findLabelFromValue(
                  orgSettings.certifications.categories,
                  record.category,
                ),
              },
            ]}
          />
        )}
        {!record.adHocCollaborator && (
          <TextBoxGrid
            numCol={2}
            items={[
              {
                label: GROUP_TARGETS_LABEL,
                text: <TargetGroups cc={record.cc} />,
              },
              {
                label: INTERNAL_GRADE_IT_LABEL,
                text:
                  record.internalGrade && record.internalGrade.length > 0
                    ? record.internalGrade.join(", ")
                    : EMPTY_STRING,
              },
            ]}
          />
        )}
        {record.adHocCollaborator ? (
          <TextBoxGrid
            numCol={isMobile ? 2 : 3}
            items={[
              {
                label: RECIPIENT_LABEL,
                text: record.collaborator
                  ? `${record.collaborator.surname} ${record.collaborator.name}`
                  : EMPTY_STRING,
              },
              {
                label: EXECUTION_MODE_LABEL,
                text: findLabelFromValue(
                  orgSettings.certifications.executionModes,
                  record.executionMode,
                ),
              },
              ...(!isMobile ? [{ label: URL_LABEL, text: record.url }] : []),
            ]}
          />
        ) : (
          <TextBoxGrid
            numCol={2}
            items={[
              {
                label: EXECUTION_MODE_LABEL,
                text: findLabelFromValue(
                  orgSettings.certifications.executionModes,
                  record.executionMode,
                ),
              },
              ...(!isMobile ? [{ label: URL_LABEL, text: record.url }] : []),
            ]}
          />
        )}
        {isMobile && <TextBox label={URL_LABEL} text={record.url} />}
        <TextBox label={DESCRIPTION_LABEL} text={record.description} />
      </>
    );
  };

  return (
    <>
      <StyledCertifications>
        {isLoading && hasData === null ? (
          <Loader loading={true}>
            <LoadingThumbnail marginTop={316} />
          </Loader>
        ) : null}
        {!isLoading && hasData === false ? (
          <div className="empty-content">
            <EmptyState
              label={EMPTY_STATE_CERTIFICATIONS_MESSAGE}
              imageSrc={certificatesImage}
            >
              {roleHasPermission(
                profile,
                PermissionsEnum.EDIT_TECH_FORMATION,
              ) && (
                <Button
                  label={CERTIFICATION_LABEL}
                  variant={ButtonVariants.SECONDARY}
                  size={ButtonSizes.REGULAR}
                  icon={<AddIcon />}
                  onClick={() => setOpenCertificationModal(true)}
                />
              )}
            </EmptyState>
          </div>
        ) : null}
        {hasData ? (
          <>
            <div className="certifications-header">
              <DoubleStructure
                verticalAlignment={AlignmentEnum.CENTER}
                variant="primary"
              >
                <div>
                  {data !== null ? (
                    <BodyTyp variant="2" fontWeight={FontWeightEnum.MEDIUM}>
                      {`${data?.pagination.total} ${CERTIFICATIONS_LABEL}`}
                    </BodyTyp>
                  ) : null}
                </div>
                {(data || searchCertifications.length > 0) && (
                  <div className="filters-container">
                    <div className="search-container">
                      <SearchInput
                        value={searchCertifications}
                        onChange={(value) =>
                          dispatch(setSearchCertification(value))
                        }
                        label={NAME_LABEL}
                        name={"search"}
                      />
                    </div>
                    <FiltersPanel
                      data={filtersData}
                      setSelectedFilters={handleFilters}
                      selectedFilters={filtersCertifications}
                      filtersTitles={getCertificationsFiltersTitles()}
                      loading={filterLoading}
                    />
                  </div>
                )}
              </DoubleStructure>
              <FiltersSelected
                data={joinFilters}
                filters={filtersCertifications}
                setFilters={handleFilters}
              />
            </div>
            <div className="certifications-content">
              <Table<Certification>
                loading={isFetching}
                headers={getCertificationsTableColumns(orgSettings, isMobile)}
                scrollY={calcTableHeight()}
                {...(roleHasPermission(
                  profile,
                  PermissionsEnum.EDIT_TECH_FORMATION,
                ) && {
                  onEdit: (record) => onEdit(record),
                  onDelete: (record) => onDelete(record),
                })}
                data={data?.data ?? []}
                rowKey={"id"}
                pagination={{
                  current: paginationCertifications + 1,
                  onChange: (pageNumber) =>
                    dispatch(setPaginationCertification(pageNumber - 1)),
                  total: data?.pagination.total ?? 0,
                  pageSize: CERTIFICATIONS_PAGE_SIZE,
                }}
                expandable={{
                  expandedRowRender: expandedRow,
                }}
                emptyText={EMPTY_CERTIFICATIONS_MESSAGE}
              />
            </div>
          </>
        ) : null}
      </StyledCertifications>
      {openCertificationModal && (
        <CertificationModal
          isOpen={openCertificationModal}
          closeModal={() => {
            setSelectedRecord(null);
            setOpenCertificationModal(false);
          }}
          {...(selectedRecord && {
            record: selectedRecord,
          })}
        />
      )}
      <PopupConfirm
        isOpen={openDeleteModal}
        onCancel={() => setOpenDeleteModal(false)}
        onConfirm={
          selectedRecord
            ? () => deleteCertification(selectedRecord?.id)
            : () => {}
        }
        label={DELETE_CERTIFICATION_LABEL}
        popupText={
          selectedRecord
            ? `${DELETE_CERTIFICATION_CONFIRM_MESSAGE} ${selectedRecord.name}?`
            : "error"
        }
        isLoading={isDeleting}
      />
    </>
  );
};
